import React, { useState, useMemo, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, XMarkIcon } from '@heroicons/react/24/outline';
import DesignTokens from '../../styles/DesignTokens';

const GalleryContainer = styled.div`
  position: relative;
  width: 100%;
  margin: ${DesignTokens.spacing.lg} 0;
`;

const MainImageContainer = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: ${DesignTokens.borderRadius.lg};
  overflow: hidden;
  background: ${props => props.theme.colors.gradientPrimary};
  cursor: pointer;
`;

const MainImage = styled(motion.img)`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ThumbnailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
  gap: ${DesignTokens.spacing.sm};
  margin-top: ${DesignTokens.spacing.md};
`;

const Thumbnail = styled(motion.div)`
  position: relative;
  aspect-ratio: 1;
  border-radius: ${DesignTokens.borderRadius.md};
  overflow: hidden;
  cursor: pointer;
  border: 2px solid ${props => props.active ? props.theme.colors.primary : 'transparent'};
  opacity: ${props => props.active ? 1 : 0.7};
  transition: all 0.2s ease;

  &:hover {
    opacity: 1;
    transform: scale(1.05);
  }
`;

const ThumbnailImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const NavigationButton = styled(motion.button)`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  min-width: 40px;
  min-height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  box-shadow: ${DesignTokens.shadow.md};
  opacity: 0;
  transition: opacity 0.2s ease, transform 0.2s ease;
  flex-shrink: 0;

  ${MainImageContainer}:hover & {
    opacity: 1;
  }

  &:hover {
    background: white;
    transform: translateY(-50%) scale(1.05);
  }

  svg {
    width: 24px;
    height: 24px;
    color: ${props => props.theme.colors.text || '#222'};
    flex-shrink: 0;
    stroke-width: 2;
  }
`;

const PrevButton = styled(NavigationButton)`
  left: ${DesignTokens.spacing.md};
`;

const NextButton = styled(NavigationButton)`
  right: ${DesignTokens.spacing.md};
`;

const ImageCounter = styled.div`
  position: absolute;
  bottom: ${DesignTokens.spacing.md};
  right: ${DesignTokens.spacing.md};
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(10px);
  color: white;
  padding: ${DesignTokens.spacing.xs} ${DesignTokens.spacing.sm};
  border-radius: ${DesignTokens.borderRadius.full};
  font-size: ${DesignTokens.typography.fontSize.sm};
  font-weight: ${DesignTokens.typography.fontWeight.semibold};
  z-index: 10;
`;

// Lightbox Modal
const LightboxOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.95);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LightboxContent = styled(motion.div)`
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LightboxImage = styled(motion.img)`
  max-width: 100%;
  max-height: 90vh;
  object-fit: contain;
  border-radius: ${DesignTokens.borderRadius.lg};
`;

const CloseButton = styled(motion.button)`
  position: absolute;
  top: ${DesignTokens.spacing.lg};
  right: ${DesignTokens.spacing.lg};
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: none;
  border-radius: ${DesignTokens.borderRadius.full};
  width: 75px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10000;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }

  svg {
    width: 24px;
    height: 24px;
    color: white;
  }
`;

function ImageGallery({ images, mainImage }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [brokenImages, setBrokenImages] = useState(new Set());

  // Combine main image with additional images, ensuring no duplicates
  // Use Set to deduplicate URLs
  const allImages = useMemo(() => {
    const imageSet = new Set();
    const result = [];
    
    // Add mainImage first if it exists and is not already in images
    if (mainImage && mainImage.trim()) {
      if (!images?.some(img => img === mainImage)) {
        imageSet.add(mainImage);
        result.push(mainImage);
      }
    }
    
    // Add images array, skipping duplicates
    if (images && Array.isArray(images)) {
      images.forEach(img => {
        if (img && img.trim() && !imageSet.has(img)) {
          imageSet.add(img);
          result.push(img);
        }
      });
    }
    
    // If no images at all but we have mainImage, use it
    if (result.length === 0 && mainImage && mainImage.trim()) {
      result.push(mainImage);
    }
    
    return result;
  }, [images, mainImage]);

  // Filter out broken images
  // Convert Set to array for dependency comparison (Sets can't be used in dependency arrays)
  const brokenImagesArray = useMemo(() => Array.from(brokenImages).sort(), [brokenImages]);
  const validImages = useMemo(() => 
    allImages.filter(img => img && !brokenImagesArray.includes(img)),
    [allImages, brokenImagesArray]
  );

  const handleLightboxPrev = useCallback((e) => {
    e.stopPropagation();
    setLightboxIndex((prev) => (prev === 0 ? validImages.length - 1 : prev - 1));
  }, [validImages.length]);

  const handleLightboxNext = useCallback((e) => {
    e.stopPropagation();
    setLightboxIndex((prev) => (prev === validImages.length - 1 ? 0 : prev + 1));
  }, [validImages.length]);

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  // Handle image load errors
  const handleImageError = (imageUrl) => {
    setBrokenImages(prev => new Set([...prev, imageUrl]));
    // If current image is broken, move to next valid image
    if (validImages[currentIndex] === imageUrl && validImages.length > 1) {
      const nextIndex = (currentIndex + 1) % validImages.length;
      setCurrentIndex(nextIndex);
    }
  };

  // Keyboard navigation - must be before early returns
  useEffect(() => {
    if (!lightboxOpen) return;

    const handleKeyPress = (e) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') handleLightboxPrev(e);
      if (e.key === 'ArrowRight') handleLightboxNext(e);
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [lightboxOpen, lightboxIndex, validImages.length, handleLightboxNext, handleLightboxPrev]);

  // Update current index if it's out of bounds after filtering
  useEffect(() => {
    if (currentIndex >= validImages.length && validImages.length > 0) {
      setCurrentIndex(0);
    }
  }, [validImages.length, currentIndex]);

  if (validImages.length === 0) {
    return null;
  }

  const currentImage = validImages[currentIndex];

  const handlePrev = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === 0 ? validImages.length - 1 : prev - 1));
  };

  const handleNext = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === validImages.length - 1 ? 0 : prev + 1));
  };

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  return (
    <>
      <GalleryContainer>
        <MainImageContainer onClick={() => openLightbox(currentIndex)}>
          <AnimatePresence mode="wait">
            <MainImage
              key={currentIndex}
              src={currentImage}
              srcSet={`
                ${currentImage}?w=800 800w,
                ${currentImage}?w=1200 1200w,
                ${currentImage}?w=1920 1920w
              `}
              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 90vw, 1200px"
              alt={`Image ${currentIndex + 1}`}
              loading={currentIndex === 0 ? "eager" : "lazy"}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              onError={() => handleImageError(currentImage)}
            />
          </AnimatePresence>
          
          {validImages.length > 1 && (
            <>
              <PrevButton
                onClick={handlePrev}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <ChevronLeftIcon />
              </PrevButton>
              <NextButton
                onClick={handleNext}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <ChevronRightIcon />
              </NextButton>
              <ImageCounter>
                {currentIndex + 1} / {validImages.length}
              </ImageCounter>
            </>
          )}
        </MainImageContainer>

        {validImages.length > 1 && (
          <ThumbnailGrid>
            {validImages.map((image, index) => (
              <Thumbnail
                key={index}
                active={index === currentIndex}
                onClick={() => setCurrentIndex(index)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <ThumbnailImage 
                  src={image} 
                  srcSet={`
                    ${image}?w=80 80w,
                    ${image}?w=160 160w
                  `}
                  sizes="80px"
                  alt={`Thumbnail ${index + 1}`}
                  loading="lazy"
                  onError={() => handleImageError(image)}
                />
              </Thumbnail>
            ))}
          </ThumbnailGrid>
        )}
      </GalleryContainer>

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxOpen && (
          <LightboxOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeLightbox}
          >
            <CloseButton
              onClick={closeLightbox}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <XMarkIcon />
            </CloseButton>
            
            <LightboxContent onClick={(e) => e.stopPropagation()}>
              <AnimatePresence mode="wait">
                {validImages[lightboxIndex] && (
                  <LightboxImage
                    key={lightboxIndex}
                    src={validImages[lightboxIndex]}
                    alt={`Image ${lightboxIndex + 1}`}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.3 }}
                    onError={() => {
                      handleImageError(validImages[lightboxIndex]);
                      if (validImages.length > 1) {
                        const nextIndex = (lightboxIndex + 1) % validImages.length;
                        setLightboxIndex(nextIndex);
                      } else {
                        closeLightbox();
                      }
                    }}
                  />
                )}
              </AnimatePresence>
              
              {validImages.length > 1 && (
                <>
                  <PrevButton
                    onClick={handleLightboxPrev}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    style={{ position: 'absolute', left: '2rem' }}
                  >
                    <ChevronLeftIcon />
                  </PrevButton>
                  <NextButton
                    onClick={handleLightboxNext}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    style={{ position: 'absolute', right: '2rem' }}
                  >
                    <ChevronRightIcon />
                  </NextButton>
                  <ImageCounter style={{ position: 'absolute', bottom: '2rem' }}>
                    {lightboxIndex + 1} / {validImages.length}
                  </ImageCounter>
                </>
              )}
            </LightboxContent>
          </LightboxOverlay>
        )}
      </AnimatePresence>
    </>
  );
}

export default ImageGallery;

